/**
 * istanbul ignore file
 *
 * @format
 */

import { Observable, throwError, timer } from 'rxjs';
import { finalize, mergeMap } from 'rxjs/operators';

import { Logger } from '../logger.service';

export interface IHTTPScalingRetryStrategy {
	excludedStatusCodes: number[];
	logger: Logger;
	maxRetryAttempts: number;
	scalingDuration: number;
}

const DEFAULT_EXCLUDED_STATUS_CODES = [];
const DEFAULT_MAX_RETRY_ATTEMPTS = 2;
const DEFAULT_SCALING_DURATION = 2;
const DEFAULT_LOG_PREFIX = 'HTTP_SCALING_RETRY_STRATEGY: ';

/**
 * @description
 * Generic scaling retry strategy for XHRs. It will take the max number of retries and scaling duration (in milliseconds) and will
 * continue to retry the source observable until the max number of retry attempts have been met, and each retry will scale in duration
 * by the retry attempt. For example: The max retries is 3 and the scaling duration is 1000, so the first retry will occur after 1s
 * and the second retry will occur after 2s and the third retry will occur after 3s and so forth.
 *
 * @param maxRetryAttempts
 * @param scalingDuration
 * @param excludedStatusCodes
 * @param logger
 *
 * TODO -- Add spec file and testing for this custom RxJS operator
 */
export const httpScalingRetryStrategy =
	({
		maxRetryAttempts = DEFAULT_MAX_RETRY_ATTEMPTS,
		scalingDuration = DEFAULT_SCALING_DURATION,
		excludedStatusCodes = DEFAULT_EXCLUDED_STATUS_CODES,
		logger
	}: IHTTPScalingRetryStrategy) =>
	(sourceError: Observable<any>) => {
		// Log the parameters
		logger.log(
			`${DEFAULT_LOG_PREFIX}HTTP scaling retry strategy initiated -- max retry attempts: ${maxRetryAttempts}, scaling duration: ${scalingDuration}, excluded status codes: ${excludedStatusCodes.join()}`
		);

		// XHR url
		let RETRY_URL: string;

		return sourceError.pipe(
			mergeMap((error, previousRetryAttempt) => {
				// Increment attempt number by 1
				const currentRetryAttempt = previousRetryAttempt + 1;

				// Get the URL from the error object
				RETRY_URL = error.url;

				/*
				 * If the maximum number of retries have been met OR if the HTTP Response status code is excluded then throw an error
				 */
				if (currentRetryAttempt > maxRetryAttempts) {
					logger.warn(
						`${DEFAULT_LOG_PREFIX}The maximum number of retry attempts have been made for URL ${RETRY_URL}, returning source error`
					);

					// Return the error Observable
					return throwError(error);
				} else if (excludedStatusCodes.indexOf(error.status) > -1) {
					logger.warn(
						`${DEFAULT_LOG_PREFIX}The HTTP Response status code ${error.status} for URL ${RETRY_URL} is not eligible for a retry attempt, returning source error`
					);

					// Return the error Observable
					return throwError(error);
				}

				logger.log(
					`${DEFAULT_LOG_PREFIX}Retry attempt number ${currentRetryAttempt} for URL ${RETRY_URL}, retrying in ${currentRetryAttempt * scalingDuration}ms`
				);

				// Return the scaled duration (current retry number * scaling duration)
				return timer(currentRetryAttempt * scalingDuration);
			}),
			finalize(() => {
				logger.log(
					`${DEFAULT_LOG_PREFIX}HTTP scaling retry strategy has completed for URL ${RETRY_URL}`
				);
			})
		);
	};
